import { useContext } from "react";
import {
  AddressBookDeleteContext,
  AddressBookHandlerContext,
  AddressBookIndexContext,
  AddressBookDeleteContextValue,
  AddressBookHandlerContextValue,
  AddressBookIndexContextValue,
} from "./index";

/* ===========================================================================
 * HOOK: useAddressBookIndexContext
 * =========================================================================== */

/**
 * @function useAddressBookIndexContext
 * Retrieves the value of the AddressBookIndexContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an AddressBookIndexProvider. If not, an error is thrown to prevent
 * unintended usage without the required context.
 *
 * @returns {AddressBookIndexContextValue} The current context value.
 * @throws {Error} If called outside of an AddressBookIndexProvider.
 */
export const useAddressBookIndexContext = (): AddressBookIndexContextValue => {
  const context = useContext(AddressBookIndexContext);
  if (!context) {
    throw new Error(
      "useAddressBookIndexContext must be used within an AddressBookIndexProvider"
    );
  }
  return context;
};

/* ===========================================================================
 * HOOK: useAddressBookHandlerContext
 * =========================================================================== */

/**
 * @function useAddressBookHandlerContext
 * Retrieves the value of the AddressBookHandlerContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an AddressBookHandlerProvider. If not, an error is thrown to enforce
 * correct usage and avoid undefined state access.
 *
 * @returns {AddressBookHandlerContextValue} The current handler context value.
 * @throws {Error} If called outside of an AddressBookHandlerProvider.
 */
export const useAddressBookHandlerContext =
  (): AddressBookHandlerContextValue => {
    const context = useContext(AddressBookHandlerContext);
    if (!context) {
      throw new Error(
        "useAddressBookHandlerContext must be used within an AddressBookHandlerProvider"
      );
    }
    return context;
  };

/* ===========================================================================
 * HOOK: useAddressBookDeleteContext
 * =========================================================================== */

/**
 * @function useAddressBookDeleteContext
 * Retrieves the value of the AddressBookDeleteContext.
 *
 * This Hook ensures that the calling component is wrapped inside
 * an AddressBookDeleteProvider. Attempting to use it outside of its
 * provider will result in an explicit contextual usage error.
 *
 * @returns {AddressBookDeleteContextValue} The current delete-context value.
 * @throws {Error} If called outside of an AddressBookDeleteProvider.
 */
export const useAddressBookDeleteContext =
  (): AddressBookDeleteContextValue => {
    const context = useContext(AddressBookDeleteContext);
    if (!context) {
      throw new Error(
        "useAddressBookDeleteContext must be used within an AddressBookDeleteProvider"
      );
    }
    return context;
  };
